import React from 'react';
import { useIntl } from 'react-intl';
import {
  Card, CardBody, CardFooter, CardHeader, Col,
  FormGroup, Row,
} from 'reactstrap';
import styled from 'styled-components';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';

const Shimmer = styled.span.attrs({
  className: 'placeholder placeholder-glow bg-darken',
})`
  height: ${(props) => props.height || '1rem'};
  width: ${(props) => props.width || '100%'};
  display: inline-block;
  border-radius: 0.25rem;
`;

/**
 * Placeholder component that displays a loading skeleton for the
 * TradeRuleProposeNominated page. Mirrors the layout of `index.jsx`: breadcrumbs,
 * heading with property controls, and the flat pricing form skeleton.
 * @returns {React.ReactElement} TradeRuleProposeNominatedPlaceholder component.
 */
function TradeRuleProposeNominatedPlaceholder() {
  const intl = useIntl();

  const propertiesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.placeholder.breadcrumbs.properties.label',
    defaultMessage: 'Properties',
  });
  const tradeRulesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.placeholder.breadcrumbs.trade_rules.label',
    defaultMessage: 'Trade Rules',
  });
  const proposePeerToPeerBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.placeholder.breadcrumbs.propose_peer_to_peer_trade_rule.label',
    defaultMessage: 'Propose peer-to-peer trade rule',
  });
  const tradeRuleProposeContextLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.placeholder.trade_rule_propose_peer_to_peer.title',
    defaultMessage: 'Trade Rule - Propose Peer-to-Peer',
  });
  const proposePeerToPeerTradeHeading = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.placeholder.flat_pricing_form.propose_peer_to_peer_trade.heading.label',
    defaultMessage: 'Propose a peer-to-peer trade',
  });

  return (
    <div aria-hidden="true" inert>
      <Breadcrumbs
        breadcrumbs={[
          { name: propertiesBreadcrumbLabel, path: '/properties' },
          { name: <Shimmer width="10rem" /> },
          { name: tradeRulesBreadcrumbLabel },
          { name: proposePeerToPeerBreadcrumbLabel },
        ]}
      />
      <Heading
        title={<Shimmer width="12rem" />}
        subtitle={<Shimmer width="18rem" />}
        context={tradeRuleProposeContextLabel}
        controls={(
          <div className="d-flex">
            <Shimmer width="8rem" height="2.25rem" />
            <div className="ms-2">
              <Shimmer width="8rem" height="2.25rem" />
            </div>
          </div>
        )}
      />

      <Card>
        <CardHeader className="d-flex flex-wrap">
          <h2 className="mb-0">{proposePeerToPeerTradeHeading}</h2>
          <div className="ms-auto">
            <Shimmer width="8rem" height="2.25rem" />
          </div>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Shimmer height="2.375rem" />
          </FormGroup>
          <FormGroup>
            <Shimmer height="2.375rem" />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Shimmer height="2.375rem" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Shimmer height="2.375rem" />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
        <CardFooter className="d-flex">
          <div className="me-2">
            <Shimmer width="6rem" height="2.25rem" />
          </div>
          <div>
            <Shimmer width="6rem" height="2.25rem" />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export default TradeRuleProposeNominatedPlaceholder;
